const RELOAD_REASON_KEY = 'reload-tracker:reason'

export type ReloadReason =
  | 'user'
  | 'forced-app-version'
  | 'app-update'
  | 'service-worker'
  | 'network-reconnected'
  | 'error-boundary'
  | 'refresh-all-data'
  | 'realtime-load-cancelled'

/**
 * Use instead of window.location.reload() so PageReloaded events can distinguish
 * reloads triggered by the app from ones triggered by the user.
 */
export function reloadPage(reason: Exclude<ReloadReason, 'user'>) {
  try {
    sessionStorage.setItem(RELOAD_REASON_KEY, reason)
  } catch {
    // storage unavailable; the reload will be reported as 'user'
  }

  window.location.reload()
}

export function consumeReloadReason(): ReloadReason {
  try {
    const reason = sessionStorage.getItem(RELOAD_REASON_KEY) as ReloadReason | null
    sessionStorage.removeItem(RELOAD_REASON_KEY)

    return reason ?? 'user'
  } catch {
    return 'user'
  }
}

function isNavigationPerformanceEntry(entry: PerformanceEntry): entry is PerformanceNavigationTiming {
  return entry.entryType === 'navigation'
}

function isReloadPerformanceEntry(entry: PerformanceEntry) {
  if (isNavigationPerformanceEntry(entry)) {
    return entry.type === 'reload'
  }

  return false
}

/**
 * Whether the current page load came from a reload. Stays true for the lifetime of the page.
 */
export function isReloadEvent() {
  const navEntries = 'getEntriesByType' in window.performance ? window.performance.getEntriesByType('navigation') : null

  return (
    (Array.isArray(navEntries) && navEntries.some(isReloadPerformanceEntry)) ||
    // compare against TYPE_RELOAD's value (1); the deprecated PerformanceNavigation global is missing in some
    // environments (e.g. jsdom), and referencing it there throws
    window.performance.navigation?.type === 1
  )
}
